<template>
  <div v-if="isAdmin" class="newsletters-admin-page clipr-theme" :class="{ 'twitch-theme': isTwitchUser }">
    <card class="newsletters-card">
      <div slot="header" class="d-flex flex-wrap align-items-center justify-content-between newsletters-header">
        <div>
          <h4 class="card-title mb-1">{{ tt("newslettersAdmin.title", "Emails") }}</h4>
          <p class="newsletters-subtitle mb-0">
            {{ tt("newslettersAdmin.subtitle", "Administra newsletters y marketing de Clipr.") }}
          </p>
        </div>
        <div v-if="activeEmailTab === 'newsletters'" class="newsletters-header-actions d-flex flex-wrap align-items-center mt-2 mt-md-0">
          <button class="btn btn-outline-primary btn-sm btn-round newsletters-btn-generate" :disabled="loading || saving" @click="openGenerateModal">
            <i class="fa-solid fa-wand-magic-sparkles mr-1"></i>
            {{ tt("newslettersAdmin.generate", "Generar con IA") }}
          </button>
          <button class="btn btn-outline-primary btn-sm btn-round" :disabled="loading || saving" @click="openCreateModal">
            <i class="ti-plus mr-1"></i>
            {{ tt("newslettersAdmin.create", "Nueva newsletter") }}
          </button>
          <button class="btn btn-primary btn-sm btn-round" :disabled="loading" @click="loadNewsletters">
            <i class="ti-reload mr-1" :class="{ 'ti-spin': loading }"></i>
            {{ loading ? tt("newslettersAdmin.reloading", "Recargando...") : tt("newslettersAdmin.reload", "Recargar") }}
          </button>
        </div>
      </div>

      <div slot="raw-content" class="newsletters-body">
        <div class="email-admin-tabs" role="tablist" :aria-label="tt('newslettersAdmin.tabsAria', 'Secciones de Emails')">
          <button
            v-for="tab in emailTabOptions"
            :key="tab.value"
            type="button"
            class="email-admin-tab"
            :class="{ active: activeEmailTab === tab.value }"
            :aria-pressed="activeEmailTab === tab.value"
            @click="setActiveEmailTab(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <template v-if="activeEmailTab === 'newsletters'">
          <div v-if="errorMessage" class="alert alert-danger mb-3">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="alert alert-success mb-3">
            {{ successMessage }}
          </div>

          <div class="newsletters-filters">
            <div class="newsletters-filter-label">
              {{ tt("newslettersAdmin.filterByStatus", "Filtrar por estado") }}
            </div>
            <div class="newsletters-filter-tabs" role="tablist">
              <button
                v-for="option in statusFilterOptions"
                :key="option.value || 'all'"
                type="button"
                class="newsletters-filter-tab"
                :class="{ active: statusFilter === option.value }"
                :aria-pressed="statusFilter === option.value"
                @click="setStatusFilter(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div v-if="loading" class="newsletters-state">
            <i class="ti-reload ti-spin"></i>
            <p>{{ tt("newslettersAdmin.loading", "Cargando newsletters...") }}</p>
          </div>

          <div v-else-if="newsletters.length === 0" class="newsletters-state">
            <i class="fa-solid fa-envelope-open-text"></i>
            <p>{{ tt("newslettersAdmin.empty", "No hay newsletters disponibles.") }}</p>
            <button class="btn btn-primary btn-sm btn-round mt-3" :disabled="saving" @click="openCreateModal">
              <i class="ti-plus mr-1"></i>
              {{ tt("newslettersAdmin.createFirst", "Crear primera newsletter") }}
            </button>
          </div>

          <div v-else class="table-responsive newsletters-table-responsive">
            <table class="table table-hover newsletters-table mb-0">
              <thead>
                <tr>
                  <th>{{ tt("newslettersAdmin.newsletter", "Newsletter") }}</th>
                  <th>{{ tt("newslettersAdmin.subject", "Asunto") }}</th>
                  <th>{{ tt("newslettersAdmin.status", "Estado") }}</th>
                  <th>{{ tt("newslettersAdmin.stats", "Stats") }}</th>
                  <th>{{ tt("newslettersAdmin.scheduledAt", "Programada") }}</th>
                  <th>{{ tt("newslettersAdmin.updatedAt", "Actualizada") }}</th>
                  <th>{{ tt("newslettersAdmin.actions", "Acciones") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="newsletter in newsletters" :key="newsletter.id">
                  <td>
                    <div class="newsletter-title">{{ newsletter.title }}</div>
                    <small class="text-muted">{{ formatDateTime(newsletter.created_at) }}</small>
                  </td>
                  <td class="newsletter-subject">{{ newsletter.subject }}</td>
                  <td>
                    <span class="badge newsletter-status-badge" :class="statusBadgeClass(newsletter.status)">
                      {{ statusLabel(newsletter.status) }}
                    </span>
                  </td>
                  <td>
                    <div v-if="newsletterStatsById[newsletter.id]" class="newsletter-stats-compact">
                      <span>S {{ newsletterStatsById[newsletter.id].sent || 0 }}</span>
                      <span>F {{ newsletterStatsById[newsletter.id].failed || 0 }}</span>
                      <span>O {{ newsletterStatsById[newsletter.id].opened_events || 0 }}</span>
                      <span>C {{ newsletterStatsById[newsletter.id].click_events || 0 }}</span>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>{{ formatDateTime(newsletter.scheduled_at) || "-" }}</td>
                  <td>{{ formatDateTime(newsletter.updated_at) }}</td>
                  <td>
                    <div class="newsletter-actions">
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-muted"
                        :disabled="isActionRunning(newsletter)"
                        @click="openPreviewModal(newsletter)"
                      >
                        {{ tt("newslettersAdmin.preview", "Preview") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-outline"
                        :disabled="!canEdit(newsletter) || isActionRunning(newsletter)"
                        @click="openEditModal(newsletter)"
                      >
                        {{ tt("newslettersAdmin.edit", "Editar") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-success"
                        :disabled="!canApprove(newsletter) || isActionRunning(newsletter)"
                        @click="approveItem(newsletter)"
                      >
                        {{ tt("newslettersAdmin.approve", "Aprobar") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-warning"
                        :disabled="!canReject(newsletter) || isActionRunning(newsletter)"
                        @click="rejectItem(newsletter)"
                      >
                        {{ tt("newslettersAdmin.reject", "Rechazar") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-primary"
                        :disabled="!canSchedule(newsletter) || isActionRunning(newsletter)"
                        @click="openScheduleModal(newsletter)"
                      >
                        {{ tt("newslettersAdmin.schedule", "Programar") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-accent"
                        :disabled="isActionRunning(newsletter)"
                        @click="openSendTestModal(newsletter)"
                      >
                        {{ tt("newslettersAdmin.sendTest", "Enviar test") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-muted"
                        :disabled="isActionRunning(newsletter)"
                        @click="openDeliveriesModal(newsletter)"
                      >
                        {{ tt("newslettersAdmin.deliveries", "Deliveries") }}
                      </button>
                      <button
                        type="button"
                        class="newsletter-action-btn newsletter-action-btn-danger"
                        :disabled="!canDelete(newsletter) || isActionRunning(newsletter)"
                        @click="openDeleteModal(newsletter)"
                      >
                        {{ tt("newslettersAdmin.delete", "Eliminar") }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div v-else-if="activeEmailTab === 'marketing'" class="try-marketing-panel email-marketing-panel">
          <div class="try-marketing-header">
            <div>
              <h5>{{ tt("newslettersAdmin.marketingTitle", "Marketing Winback") }}</h5>
              <p>{{ tt("newslettersAdmin.marketingBody", "Dashboard admin para monitorear la cola automatica, estados y eventos de Winback.") }}</p>
            </div>
            <div class="email-marketing-header-actions">
              <button class="btn btn-outline-primary btn-sm btn-round" :disabled="emailMarketingPreviewLoading" @click="openEmailMarketingPreviewModal">
                <i class="fa-solid fa-eye mr-1"></i>
                {{ tt("newslettersAdmin.preview", "Preview") }}
              </button>
              <button class="btn btn-primary btn-sm btn-round" :disabled="emailMarketingLoading" @click="loadEmailMarketingDashboard">
                <i class="ti-reload mr-1" :class="{ 'ti-spin': emailMarketingLoading }"></i>
                {{ emailMarketingLoading ? tt("newslettersAdmin.reloading", "Recargando...") : tt("newslettersAdmin.reload", "Recargar") }}
              </button>
            </div>
          </div>

          <div v-if="emailMarketingError" class="alert alert-danger mb-3">
            {{ emailMarketingError }}
          </div>

          <div v-if="emailMarketingSuccess" class="alert alert-success mb-3">
            {{ emailMarketingSuccess }}
          </div>

          <div v-if="emailMarketingLoading && !emailMarketingSummary" class="newsletters-state newsletters-state-compact">
            <i class="ti-reload ti-spin"></i>
            <p>{{ tt("newslettersAdmin.marketingLoading", "Cargando Marketing Winback...") }}</p>
          </div>

          <template v-else-if="emailMarketingSummary">
            <div class="try-marketing-metrics">
              <div v-for="metric in emailMarketingMetrics" :key="metric.key" class="try-marketing-metric">
                <span class="try-marketing-metric-value">{{ metric.value }}</span>
                <span class="try-marketing-metric-label">{{ metric.label }}</span>
              </div>
            </div>

            <div class="try-marketing-dates">
              <span>{{ tt("newslettersAdmin.totalStates", "Estados") }}: {{ emailMarketingSummary.total_states || 0 }}</span>
              <span>{{ tt("newslettersAdmin.totalDeliveries", "Deliveries") }}: {{ emailMarketingSummary.total_deliveries || 0 }}</span>
              <span>{{ tt("newslettersAdmin.tryLastSentAt", "Ultimo envio") }}: {{ formatDateTime(emailMarketingSummary.last_sent_at) || "-" }}</span>
              <span>{{ tt("newslettersAdmin.tryLastFailedAt", "Ultimo fallo") }}: {{ formatDateTime(emailMarketingSummary.last_failed_at) || "-" }}</span>
            </div>

            <div class="email-marketing-actions-card">
              <div>
                <h6>{{ tt("newslettersAdmin.marketingAutomationTitle", "Automatizacion activa") }}</h6>
                <p>{{ tt("newslettersAdmin.marketingAutomationHelp", "El cron sincroniza candidatos aptos y envia maximo 6 correos por corrida. Esta pantalla solo monitorea cola, estados y eventos.") }}</p>
              </div>
            </div>

            <div v-if="emailMarketingCampaignRows.length === 0" class="newsletters-state newsletters-state-compact">
              <i class="fa-solid fa-inbox"></i>
              <p>{{ tt("newslettersAdmin.marketingNoCampaigns", "No hay deliveries de Marketing registrados.") }}</p>
            </div>

            <div v-else class="table-responsive newsletters-table-responsive try-marketing-table">
              <table class="table table-hover newsletters-table mb-0">
                <thead>
                  <tr>
                    <th>{{ tt("newslettersAdmin.tryCampaign", "Campana") }}</th>
                    <th>{{ tt("newslettersAdmin.total", "Total") }}</th>
                    <th>{{ tt("newslettersAdmin.pending", "Pendientes") }}</th>
                    <th>{{ tt("newslettersAdmin.sent", "Enviados") }}</th>
                    <th>{{ tt("newslettersAdmin.cancelled", "Cancelados") }}</th>
                    <th>{{ tt("newslettersAdmin.failed", "Fallidos") }}</th>
                    <th>{{ tt("newslettersAdmin.tryLastSentAt", "Ultimo envio") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in emailMarketingCampaignRows" :key="row.campaign_key">
                    <td class="try-marketing-campaign-key">{{ row.campaign_key }}</td>
                    <td>{{ row.total_deliveries }}</td>
                    <td>{{ row.pending }}</td>
                    <td>{{ row.sent }}</td>
                    <td>{{ row.cancelled }}</td>
                    <td>{{ row.failed }}</td>
                    <td>{{ formatDateTime(row.last_sent_at) || "-" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="email-marketing-breakdowns">
              <div v-for="section in emailMarketingBreakdownSections" :key="section.key" class="email-marketing-breakdown-card">
                <h6>{{ section.title }}</h6>
                <div v-if="section.rows.length === 0" class="email-marketing-breakdown-empty">
                  {{ tt("newslettersAdmin.noData", "Sin datos") }}
                </div>
                <div v-else class="table-responsive newsletters-table-responsive">
                  <table class="table table-hover newsletters-table mb-0">
                    <thead>
                      <tr>
                        <th>{{ tt("newslettersAdmin.breakdown", "Breakdown") }}</th>
                        <th>{{ tt("newslettersAdmin.total", "Total") }}</th>
                        <th>{{ tt("newslettersAdmin.pending", "Pendientes") }}</th>
                        <th>{{ tt("newslettersAdmin.sent", "Enviados") }}</th>
                        <th>{{ tt("newslettersAdmin.cancelled", "Cancelados") }}</th>
                        <th>{{ tt("newslettersAdmin.failed", "Fallidos") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in section.rows" :key="section.key + '-' + row.key">
                        <td class="try-marketing-campaign-key">{{ emailMarketingBreakdownLabel(section.key, row.key) }}</td>
                        <td>{{ row.total_deliveries }}</td>
                        <td>{{ row.pending }}</td>
                        <td>{{ row.sent }}</td>
                        <td>{{ row.cancelled }}</td>
                        <td>{{ row.failed }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="newsletters-filters email-marketing-state-filters">
              <div class="newsletters-filter-label">
                {{ tt("newslettersAdmin.marketingStates", "Estados Marketing") }}
              </div>
              <div class="newsletters-filter-tabs" role="tablist">
                <button
                  v-for="option in emailMarketingStatusFilterOptions"
                  :key="option.value || 'all'"
                  type="button"
                  class="newsletters-filter-tab"
                  :class="{ active: emailMarketingStatusFilter === option.value }"
                  :aria-pressed="emailMarketingStatusFilter === option.value"
                  @click="setEmailMarketingStatusFilter(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
              <form class="email-marketing-state-search" @submit.prevent="searchEmailMarketingStates">
                <input
                  v-model.trim="emailMarketingEmailFilter"
                  type="email"
                  class="form-control form-control-sm"
                  :placeholder="tt('newslettersAdmin.filterByEmail', 'Filtrar por email')"
                >
                <button type="submit" class="btn btn-outline-primary btn-sm btn-round" :disabled="emailMarketingStatesLoading">
                  {{ tt("newslettersAdmin.search", "Buscar") }}
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm btn-round" :disabled="emailMarketingStatesLoading" @click="resetEmailMarketingStateFilters">
                  {{ tt("newslettersAdmin.clear", "Limpiar") }}
                </button>
              </form>
            </div>

            <div v-if="emailMarketingStatesLoading" class="newsletters-state newsletters-state-compact">
              <i class="ti-reload ti-spin"></i>
              <p>{{ tt("newslettersAdmin.marketingStatesLoading", "Cargando estados de Marketing...") }}</p>
            </div>

            <div v-else-if="emailMarketingStates.length === 0" class="newsletters-state newsletters-state-compact">
              <i class="fa-solid fa-users-slash"></i>
              <p>{{ tt("newslettersAdmin.marketingNoStates", "No hay estados de Marketing para esos filtros.") }}</p>
            </div>

            <div v-else class="table-responsive newsletters-table-responsive email-marketing-states-table">
              <table class="table table-hover newsletters-table mb-0">
                <thead>
                  <tr>
                    <th>{{ tt("newslettersAdmin.email", "Email") }}</th>
                    <th>{{ tt("newslettersAdmin.segment", "Segmento") }}</th>
                    <th>{{ tt("newslettersAdmin.tokenState", "Tokens") }}</th>
                    <th>{{ tt("newslettersAdmin.cycle", "Ciclo") }}</th>
                    <th>{{ tt("newslettersAdmin.step", "Step") }}</th>
                    <th>{{ tt("newslettersAdmin.status", "Estado") }}</th>
                    <th>{{ tt("newslettersAdmin.engagement", "Engagement") }}</th>
                    <th>{{ tt("newslettersAdmin.sentAt", "Enviado") }}</th>
                    <th>{{ tt("newslettersAdmin.openedAt", "Open") }}</th>
                    <th>{{ tt("newslettersAdmin.clickedAt", "Click") }}</th>
                    <th>{{ tt("newslettersAdmin.convertedAt", "Convertido") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="state in emailMarketingStates" :key="state.id">
                    <td class="email-marketing-email">
                      <div>{{ state.email }}</div>
                      <small class="text-muted">{{ state.user_id }}</small>
                    </td>
                    <td>{{ emailMarketingSegmentLabel(state.segment) }}</td>
                    <td>{{ emailMarketingTokenStateLabel(state.token_state) }}</td>
                    <td>{{ state.cycle }}</td>
                    <td>{{ state.current_step }}</td>
                    <td>
                      <span class="badge newsletter-status-badge" :class="emailMarketingStateStatusBadgeClass(state.status)">
                        {{ emailMarketingStateStatusLabel(state.status) }}
                      </span>
                    </td>
                    <td>{{ emailMarketingEngagementLabel(state.engagement_tier) }}</td>
                    <td>{{ formatDateTime(state.last_email_sent_at) || "-" }}</td>
                    <td>{{ formatDateTime(state.last_opened_at) || "-" }}</td>
                    <td>{{ formatDateTime(state.last_clicked_at) || "-" }}</td>
                    <td>{{ formatDateTime(state.converted_at) || "-" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </card>

    <div v-if="showEmailMarketingPreviewModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal newsletters-modal-xl" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.marketingPreviewTitle", "Preview Marketing Winback") }}</h5>
            <p class="mb-0">
              {{ tt("newslettersAdmin.marketingPreviewHelp", "Renderiza una variante winback sin enviar email.") }}
            </p>
          </div>
          <button type="button" class="close" @click="closeEmailMarketingPreviewModal">
            <span>&times;</span>
          </button>
        </div>

        <form class="newsletters-modal-body email-marketing-preview-form" @submit.prevent="runEmailMarketingPreview">
          <div class="row">
            <div class="col-md-3">
              <label>{{ tt("newslettersAdmin.segment", "Segmento") }}</label>
              <select v-model="emailMarketingPreviewForm.segment" class="form-control" required>
                <option v-for="segment in emailMarketingSegmentOptions" :key="segment" :value="segment">
                  {{ emailMarketingSegmentLabel(segment) }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <label>{{ tt("newslettersAdmin.cycle", "Ciclo") }}</label>
              <select v-model.number="emailMarketingPreviewForm.cycle" class="form-control" required>
                <option v-for="cycle in emailMarketingCycleOptions" :key="cycle" :value="cycle">
                  {{ cycle }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <label>{{ tt("newslettersAdmin.step", "Step") }}</label>
              <select v-model.number="emailMarketingPreviewForm.step" class="form-control" required>
                <option v-for="step in emailMarketingStepOptions" :key="step" :value="step">
                  {{ step }}
                </option>
              </select>
            </div>
            <div class="col-md-3">
              <label>{{ tt("newslettersAdmin.tokenState", "Tokens") }}</label>
              <select v-model="emailMarketingPreviewForm.token_state" class="form-control" required>
                <option v-for="tokenState in emailMarketingTokenStateOptions" :key="tokenState" :value="tokenState">
                  {{ emailMarketingTokenStateLabel(tokenState) }}
                </option>
              </select>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <label>{{ tt("newslettersAdmin.recipientName", "Nombre") }}</label>
              <input v-model.trim="emailMarketingPreviewForm.recipient_name" type="text" class="form-control" required>
            </div>
            <div class="col-md-6">
              <label>{{ tt("newslettersAdmin.unsubscribeUrl", "URL unsubscribe") }}</label>
              <input v-model.trim="emailMarketingPreviewForm.unsubscribe_url" type="url" class="form-control" required>
            </div>
          </div>

          <div class="newsletters-modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="emailMarketingPreviewLoading" @click="closeEmailMarketingPreviewModal">
              {{ tt("newslettersAdmin.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="emailMarketingPreviewLoading">
              {{ emailMarketingPreviewLoading ? tt("newslettersAdmin.loadingPreview", "Generando preview...") : tt("newslettersAdmin.preview", "Preview") }}
            </button>
          </div>

          <div v-if="emailMarketingPreviewSubject || emailMarketingPreviewCampaignKey" class="email-marketing-preview-meta">
            <span>{{ tt("newslettersAdmin.subject", "Asunto") }}: {{ emailMarketingPreviewSubject || "-" }}</span>
            <span>{{ tt("newslettersAdmin.campaign", "Campana") }}: {{ emailMarketingPreviewCampaignKey || "-" }}</span>
          </div>

          <div v-if="emailMarketingPreviewLoading" class="newsletters-state newsletters-state-compact">
            <i class="ti-reload ti-spin"></i>
            <p>{{ tt("newslettersAdmin.loadingPreview", "Generando preview...") }}</p>
          </div>

          <iframe
            v-else-if="emailMarketingPreviewHtml"
            class="newsletters-preview-frame email-marketing-preview-frame"
            title="Email marketing preview"
            sandbox=""
            :srcdoc="emailMarketingPreviewHtml"
          ></iframe>
        </form>
      </div>
    </div>

    <div v-if="showGenerateModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.generateTitle", "Generar newsletters con IA") }}</h5>
            <p class="mb-0">
              {{ tt("newslettersAdmin.generateHelp", "La IA solo crea borradores. Un admin debe revisarlos y aprobarlos antes de enviar.") }}
            </p>
          </div>
          <button type="button" class="close" @click="closeGenerateModal">
            <span>&times;</span>
          </button>
        </div>

        <form class="newsletters-modal-body newsletters-generate-form" @submit.prevent="generateDrafts">
          <div class="row">
            <div class="col-md-6">
              <label>{{ tt("newslettersAdmin.generateCount", "Cantidad") }}</label>
              <input v-model.number="generateForm.count" type="number" min="1" max="30" class="form-control" required>
            </div>
            <div class="col-md-6">
              <label>{{ tt("newslettersAdmin.generateLanguage", "Idioma") }}</label>
              <select v-model="generateForm.language" class="form-control" required>
                <option value="es">{{ tt("newslettersAdmin.generateLanguageEs", "Espanol") }}</option>
                <option value="en">{{ tt("newslettersAdmin.generateLanguageEn", "Ingles") }}</option>
              </select>
            </div>
          </div>
          <div class="form-group mt-3">
            <label>{{ tt("newslettersAdmin.generateTopics", "Temas") }}</label>
            <textarea
              v-model.trim="generateForm.topicsText"
              class="form-control"
              rows="5"
              required
            ></textarea>
            <small class="form-text text-muted">
              {{ tt("newslettersAdmin.generateTopicsHelp", "Escribe un tema por linea. El backend usa un prompt fijo y valida que la respuesta complete la plantilla.") }}
            </small>
          </div>
          <div class="newsletters-modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="closeGenerateModal">
              {{ tt("newslettersAdmin.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <i class="fa-solid fa-wand-magic-sparkles mr-1"></i>
              {{ saving ? tt("newslettersAdmin.generating", "Generando...") : tt("newslettersAdmin.generateDrafts", "Generar borradores") }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showEditorModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal newsletters-modal-xl" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">
              {{ isEditing ? tt("newslettersAdmin.editModalTitle", "Editar newsletter") : tt("newslettersAdmin.createModalTitle", "Crear newsletter") }}
            </h5>
            <p class="mb-0">
              {{ tt("newslettersAdmin.editorHelp", "Completa los campos usados por la plantilla HTML para que el preview y el envio no fallen.") }}
            </p>
          </div>
          <button type="button" class="close" @click="closeEditorModal">
            <span>&times;</span>
          </button>
        </div>

        <form class="newsletters-modal-body newsletters-editor-form" @submit.prevent="saveNewsletter">
          <div class="row">
            <div class="col-md-6">
              <label>{{ tt("newslettersAdmin.internalTitle", "Titulo interno") }}</label>
              <input v-model.trim="editorForm.title" type="text" class="form-control" required>
            </div>
            <div class="col-md-6">
              <label>{{ tt("newslettersAdmin.emailSubject", "Asunto del email") }}</label>
              <input v-model.trim="editorForm.subject" type="text" class="form-control" required>
            </div>
          </div>

          <div v-for="group in contentFieldGroups" :key="group.title" class="newsletters-editor-group">
            <h6>{{ group.title }}</h6>
            <div class="row">
              <div v-for="field in group.fields" :key="field.key" :class="field.wrapperClass || 'col-md-6'">
                <label>{{ field.label }}</label>
                <textarea
                  v-if="field.type === 'textarea'"
                  v-model.trim="editorForm.content_json[field.key]"
                  class="form-control"
                  rows="3"
                  required
                ></textarea>
                <input
                  v-else
                  v-model.trim="editorForm.content_json[field.key]"
                  :type="field.type || 'text'"
                  class="form-control"
                  :readonly="Boolean(field.readonly)"
                  required
                >
              </div>
            </div>
          </div>

          <div class="newsletters-modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="closeEditorModal">
              {{ tt("newslettersAdmin.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <i class="ti-save mr-1"></i>
              {{ saving ? tt("newslettersAdmin.saving", "Guardando...") : tt("newslettersAdmin.save", "Guardar") }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showPreviewModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal newsletters-modal-xl" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.previewTitle", "Preview de newsletter") }}</h5>
            <p class="mb-0">{{ selectedNewsletterTitle }}</p>
          </div>
          <button type="button" class="close" @click="closePreviewModal">
            <span>&times;</span>
          </button>
        </div>
        <div class="newsletters-modal-body">
          <div class="row newsletters-preview-controls">
            <div class="col-md-5">
              <label>{{ tt("newslettersAdmin.previewName", "Nombre para preview") }}</label>
              <input v-model.trim="previewRecipient.name" type="text" class="form-control">
            </div>
            <div class="col-md-5">
              <label>{{ tt("newslettersAdmin.previewEmail", "Email para preview") }}</label>
              <input v-model.trim="previewRecipient.email" type="email" class="form-control">
            </div>
            <div class="col-md-2 d-flex align-items-end">
              <button class="btn btn-primary btn-block" :disabled="previewLoading" @click="loadPreviewHtml">
                {{ previewLoading ? tt("newslettersAdmin.loadingShort", "Cargando...") : tt("newslettersAdmin.refresh", "Actualizar") }}
              </button>
            </div>
          </div>
          <div v-if="previewLoading" class="newsletters-state newsletters-state-compact">
            <i class="ti-reload ti-spin"></i>
            <p>{{ tt("newslettersAdmin.loadingPreview", "Generando preview...") }}</p>
          </div>
          <iframe v-else-if="previewHtml" class="newsletters-preview-frame" :srcdoc="previewHtml"></iframe>
        </div>
      </div>
    </div>

    <div v-if="showSendTestModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.sendTestTitle", "Enviar email de prueba") }}</h5>
            <p class="mb-0">{{ selectedNewsletterTitle }}</p>
          </div>
          <button type="button" class="close" @click="closeSendTestModal">
            <span>&times;</span>
          </button>
        </div>
        <form class="newsletters-modal-body" @submit.prevent="sendTestEmail">
          <div class="form-group">
            <label>{{ tt("newslettersAdmin.recipientName", "Nombre") }}</label>
            <input v-model.trim="sendTestForm.name" type="text" class="form-control" required>
          </div>
          <div class="form-group">
            <label>{{ tt("newslettersAdmin.recipientEmail", "Email") }}</label>
            <input v-model.trim="sendTestForm.email" type="email" class="form-control" required>
          </div>
          <div class="newsletters-modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="closeSendTestModal">
              {{ tt("newslettersAdmin.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? tt("newslettersAdmin.sending", "Enviando...") : tt("newslettersAdmin.send", "Enviar") }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showScheduleModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.scheduleTitle", "Programar newsletter") }}</h5>
            <p class="mb-0">{{ selectedNewsletterTitle }}</p>
          </div>
          <button type="button" class="close" @click="closeScheduleModal">
            <span>&times;</span>
          </button>
        </div>
        <form class="newsletters-modal-body" @submit.prevent="scheduleSelectedNewsletter">
          <div class="form-group">
            <label>{{ tt("newslettersAdmin.scheduledAt", "Programada") }}</label>
            <input v-model="scheduleForm.scheduled_at" type="datetime-local" class="form-control" required>
            <small class="form-text text-muted">
              {{ tt("newslettersAdmin.scheduleHelp", "Se enviara al backend como fecha UTC con timezone.") }}
            </small>
          </div>
          <div class="newsletters-modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="closeScheduleModal">
              {{ tt("newslettersAdmin.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? tt("newslettersAdmin.saving", "Guardando...") : tt("newslettersAdmin.schedule", "Programar") }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showDeliveriesModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal newsletters-modal-xl" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.deliveriesTitle", "Deliveries") }}</h5>
            <p class="mb-0">{{ selectedNewsletterTitle }}</p>
          </div>
          <button type="button" class="close" @click="closeDeliveriesModal">
            <span>&times;</span>
          </button>
        </div>
        <div class="newsletters-modal-body">
          <div class="newsletters-delivery-summary">
            <span class="badge badge-secondary">{{ tt("newslettersAdmin.total", "Total") }}: {{ selectedDeliverySummary.total }}</span>
            <span class="badge badge-warning">{{ tt("newslettersAdmin.pending", "Pendientes") }}: {{ selectedDeliverySummary.pending }}</span>
            <span class="badge badge-success">{{ tt("newslettersAdmin.sent", "Enviados") }}: {{ selectedDeliverySummary.sent }}</span>
            <span class="badge badge-danger">{{ tt("newslettersAdmin.failed", "Fallidos") }}: {{ selectedDeliverySummary.failed }}</span>
            <span class="badge badge-info">{{ tt("newslettersAdmin.opens", "Opens") }}: {{ selectedDeliverySummary.opened_events }}</span>
            <span class="badge badge-primary">{{ tt("newslettersAdmin.clicks", "Clicks") }}: {{ selectedDeliverySummary.click_events }}</span>
            <span class="badge badge-dark">{{ tt("newslettersAdmin.bounces", "Bounces") }}: {{ (selectedDeliverySummary.hard_bounce_events || 0) + (selectedDeliverySummary.soft_bounce_events || 0) }}</span>
          </div>
          <div v-if="deliveriesLoading" class="newsletters-state newsletters-state-compact">
            <i class="ti-reload ti-spin"></i>
            <p>{{ tt("newslettersAdmin.loadingDeliveries", "Cargando deliveries...") }}</p>
          </div>
          <div v-else-if="selectedDeliveries.length === 0" class="newsletters-state newsletters-state-compact">
            <i class="fa-solid fa-inbox"></i>
            <p>{{ tt("newslettersAdmin.noDeliveries", "No hay deliveries para esta newsletter.") }}</p>
          </div>
          <div v-else class="table-responsive newsletters-deliveries-table">
            <table class="table table-hover newsletters-table mb-0">
              <thead>
                <tr>
                  <th>{{ tt("newslettersAdmin.email", "Email") }}</th>
                  <th>{{ tt("newslettersAdmin.status", "Estado") }}</th>
                  <th>{{ tt("newslettersAdmin.sentAt", "Enviado") }}</th>
                  <th>{{ tt("newslettersAdmin.error", "Error") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="delivery in selectedDeliveries" :key="delivery.id">
                  <td>{{ delivery.email }}</td>
                  <td>
                    <span class="badge" :class="deliveryStatusBadgeClass(delivery.status)">
                      {{ deliveryStatusLabel(delivery.status) }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(delivery.sent_at) || "-" }}</td>
                  <td class="delivery-error">{{ delivery.error_message || "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDeleteModal" class="newsletters-modal-backdrop">
      <div class="newsletters-modal" role="dialog" aria-modal="true">
        <div class="newsletters-modal-header">
          <div>
            <h5 class="mb-1">{{ tt("newslettersAdmin.deleteTitle", "Eliminar newsletter") }}</h5>
            <p class="mb-0">{{ selectedNewsletterTitle }}</p>
          </div>
          <button type="button" class="close" :disabled="saving" @click="closeDeleteModal">
            <span>&times;</span>
          </button>
        </div>
        <div class="newsletters-modal-body">
          <p class="newsletters-delete-warning">
            {{ tt("newslettersAdmin.deleteWarning", "Esta accion eliminara permanentemente la plantilla y no se puede deshacer.") }}
          </p>
          <div class="newsletters-modal-footer">
            <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="closeDeleteModal">
              {{ tt("newslettersAdmin.cancel", "Cancelar") }}
            </button>
            <button type="button" class="btn btn-danger" :disabled="saving" @click="deleteSelectedNewsletter">
              {{ saving ? tt("newslettersAdmin.deleting", "Eliminando...") : tt("newslettersAdmin.confirmDelete", "Eliminar permanentemente") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Card } from "@/components";
import { supabase } from "@/supabase";
import { useCurrentUserStore } from "@/stores/currentUser";
import {
  approveNewsletter,
  createNewsletter,
  deleteNewsletter,
  fetchNewsletterDeliveries,
  fetchNewsletterPreview,
  fetchNewsletterSummaries,
  fetchNewsletterSummary,
  fetchNewsletters,
  generateNewsletters,
  rejectNewsletter,
  scheduleNewsletter,
  sendNewsletterTest,
  updateNewsletter,
} from "@/api/newsletters";
import {
  fetchEmailMarketingStates,
  fetchEmailMarketingSummary,
  previewEmailMarketing,
} from "@/api/emailMarketing";

const CONTENT_FIELD_GROUPS = [
  {
    title: "Encabezado",
    fields: [
      { key: "edition_title", label: "Titulo de edicion" },
      { key: "edition_number", label: "Numero de edicion" },
      { key: "edition_month", label: "Mes y anio" },
      { key: "read_time", label: "Tiempo de lectura" },
    ],
  },
  {
    title: "Introduccion",
    fields: [
      { key: "intro_paragraph_1", label: "Parrafo introductorio 1", type: "textarea", wrapperClass: "col-md-6" },
      { key: "intro_paragraph_2", label: "Parrafo introductorio 2", type: "textarea", wrapperClass: "col-md-6" },
    ],
  },
  {
    title: "Seccion principal",
    fields: [
      { key: "section_1_label", label: "Etiqueta seccion 1" },
      { key: "section_1_title", label: "Titulo seccion 1" },
      { key: "section_1_body_1", label: "Texto seccion 1 - parrafo 1", type: "textarea", wrapperClass: "col-md-6" },
      { key: "section_1_body_2", label: "Texto seccion 1 - parrafo 2", type: "textarea", wrapperClass: "col-md-6" },
      { key: "callout_label", label: "Etiqueta callout" },
      { key: "callout_text", label: "Texto callout", type: "textarea", wrapperClass: "col-md-6" },
    ],
  },
  {
    title: "Seccion secundaria",
    fields: [
      { key: "section_2_label", label: "Etiqueta seccion 2" },
      { key: "section_2_title", label: "Titulo seccion 2" },
      { key: "section_2_body", label: "Texto seccion 2", type: "textarea", wrapperClass: "col-md-12" },
      { key: "section_2_link", label: "Link seccion 2 (fijo app.clipr.tv)", type: "url", readonly: true },
      { key: "section_2_cta", label: "CTA seccion 2" },
    ],
  },
  {
    title: "Seccion final",
    fields: [
      { key: "section_3_label", label: "Etiqueta seccion 3" },
      { key: "section_3_title", label: "Titulo seccion 3" },
      { key: "section_3_body", label: "Texto seccion 3", type: "textarea", wrapperClass: "col-md-12" },
      { key: "section_3_link", label: "Link seccion 3 (fijo app.clipr.tv)", type: "url", readonly: true },
      { key: "section_3_cta", label: "CTA seccion 3" },
    ],
  },
  {
    title: "Tips rapidos",
    fields: [
      { key: "quick_1_title", label: "Tip 1 titulo" },
      { key: "quick_1_desc", label: "Tip 1 descripcion", type: "textarea", wrapperClass: "col-md-6" },
      { key: "quick_2_title", label: "Tip 2 titulo" },
      { key: "quick_2_desc", label: "Tip 2 descripcion", type: "textarea", wrapperClass: "col-md-6" },
      { key: "quick_3_title", label: "Tip 3 titulo" },
      { key: "quick_3_desc", label: "Tip 3 descripcion", type: "textarea", wrapperClass: "col-md-6" },
    ],
  },
];

const STATUS_OPTIONS = ["draft", "approved", "rejected", "scheduled", "sending", "sent", "cancelled"];
const EMAIL_MARKETING_STATUS_OPTIONS = ["active", "cooldown", "converted", "exhausted", "paused"];
const EMAIL_MARKETING_SEGMENT_OPTIONS = ["generated", "not_generated"];
const EMAIL_MARKETING_TOKEN_STATE_OPTIONS = ["has_tokens", "no_tokens"];
const DELIVERY_PAGE_SIZE = 500;
const DELIVERY_MAX_PAGES = 20;

function getContentFields() {
  return CONTENT_FIELD_GROUPS.reduce((fields, group) => fields.concat(group.fields), []);
}

function buildDefaultContent() {
  return getContentFields().reduce((content, field) => {
    content[field.key] = "";
    return content;
  }, {});
}

function buildEditorForm(newsletter = null) {
  const content = buildDefaultContent();
  const sourceContent = newsletter?.content_json && typeof newsletter.content_json === "object"
    ? newsletter.content_json
    : {};

  Object.keys(content).forEach((key) => {
    content[key] = sourceContent[key] === undefined || sourceContent[key] === null
      ? ""
      : String(sourceContent[key]);
  });

  content.section_2_link = "https://app.clipr.tv";
  content.section_3_link = "https://app.clipr.tv";

  return {
    title: newsletter?.title || "",
    subject: newsletter?.subject || "",
    content_json: content,
  };
}

function buildDefaultScheduleLocal() {
  const date = new Date(Date.now() + 15 * 60 * 1000);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function buildDefaultGenerateForm() {
  return {
    count: 3,
    language: "es",
    topicsText: [
      "como convertir un VOD de Twitch o Kick en clips verticales con Clipr",
      "como usar subtítulos y layouts para publicar en TikTok, Reels y Shorts",
      "como programar posts y revisar analytics de clips en Clipr",
    ].join("\n"),
  };
}

function buildDefaultEmailMarketingPreviewForm() {
  return {
    segment: "generated",
    cycle: 1,
    step: 1,
    token_state: "no_tokens",
    recipient_name: "Admin",
    unsubscribe_url: "https://app.clipr.tv/newsletters/unsubscribe?token=preview",
  };
}

export default {
  name: "NewslettersAdmin",
  components: {
    Card,
  },
  data() {
    return {
      isAdmin: false,
      isTwitchUser: false,
      session: null,
      activeEmailTab: "newsletters",
      loading: false,
      saving: false,
      previewLoading: false,
      deliveriesLoading: false,
      emailMarketingLoading: false,
      emailMarketingStatesLoading: false,
      emailMarketingPreviewLoading: false,
      actionNewsletterId: null,
      newsletters: [],
      emailMarketingSummary: null,
      emailMarketingStates: [],
      emailMarketingStatesTotal: 0,
      emailMarketingStatusFilter: "",
      emailMarketingEmailFilter: "",
      emailMarketingPreviewForm: buildDefaultEmailMarketingPreviewForm(),
      emailMarketingPreviewHtml: "",
      emailMarketingPreviewSubject: "",
      emailMarketingPreviewCampaignKey: "",
      emailMarketingSegmentOptions: EMAIL_MARKETING_SEGMENT_OPTIONS,
      emailMarketingTokenStateOptions: EMAIL_MARKETING_TOKEN_STATE_OPTIONS,
      emailMarketingCycleOptions: [1, 2],
      emailMarketingStepOptions: [1, 2, 3, 4, 5],
      statusFilter: "",
      errorMessage: "",
      successMessage: "",
      emailMarketingError: "",
      emailMarketingSuccess: "",
      showEditorModal: false,
      showPreviewModal: false,
      showEmailMarketingPreviewModal: false,
      showSendTestModal: false,
      showScheduleModal: false,
      showDeliveriesModal: false,
      showGenerateModal: false,
      showDeleteModal: false,
      isEditing: false,
      selectedNewsletter: null,
      editorForm: buildEditorForm(),
      generateForm: buildDefaultGenerateForm(),
      previewRecipient: {
        name: "",
        email: "",
      },
      previewHtml: "",
      sendTestForm: {
        name: "",
        email: "",
      },
      scheduleForm: {
        scheduled_at: buildDefaultScheduleLocal(),
      },
      selectedDeliveries: [],
      selectedDeliverySummary: {
        total: 0,
        pending: 0,
        sent: 0,
        failed: 0,
        opened_events: 0,
        click_events: 0,
        hard_bounce_events: 0,
        soft_bounce_events: 0,
        spam_events: 0,
        unsubscribed_events: 0,
      },
      newsletterStatsById: {},
      contentFieldGroups: CONTENT_FIELD_GROUPS,
    };
  },
  computed: {
    emailTabOptions() {
      return [
        { value: "newsletters", label: this.tt("newslettersAdmin.tabNewsletters", "Newsletters") },
        { value: "marketing", label: this.tt("newslettersAdmin.tabMarketing", "Marketing") },
      ];
    },
    statusFilterOptions() {
      return [
        { value: "", label: this.tt("newslettersAdmin.statusAll", "Todos") },
        ...STATUS_OPTIONS.map((status) => ({
          value: status,
          label: this.statusLabel(status),
        })),
      ];
    },
    selectedNewsletterTitle() {
      return this.selectedNewsletter?.title || "";
    },
    emailMarketingStatusFilterOptions() {
      return [
        { value: "", label: this.tt("newslettersAdmin.statusAll", "Todos") },
        ...EMAIL_MARKETING_STATUS_OPTIONS.map((status) => ({
          value: status,
          label: this.emailMarketingStateStatusLabel(status),
        })),
      ];
    },
    emailMarketingMetrics() {
      const summary = this.emailMarketingSummary || {};
      return [
        { key: "active", label: this.emailMarketingStateStatusLabel("active"), value: Number(summary.active || 0) },
        { key: "cooldown", label: this.emailMarketingStateStatusLabel("cooldown"), value: Number(summary.cooldown || 0) },
        { key: "conversions", label: this.tt("newslettersAdmin.marketingConversions", "Conversiones"), value: Number(summary.conversions || summary.converted || 0) },
        { key: "exhausted", label: this.emailMarketingStateStatusLabel("exhausted"), value: Number(summary.exhausted || 0) },
        { key: "sent", label: this.tt("newslettersAdmin.sent", "Enviados"), value: Number(summary.sent || 0) },
        { key: "pending", label: this.tt("newslettersAdmin.pending", "Pendientes"), value: Number(summary.pending || 0) },
        { key: "failed", label: this.tt("newslettersAdmin.failed", "Fallidos"), value: Number(summary.failed || 0) },
        { key: "opens", label: this.tt("newslettersAdmin.marketingOpens", "Opens"), value: Number(summary.opened_events || 0) },
        { key: "clicks", label: this.tt("newslettersAdmin.marketingClicks", "Clicks"), value: Number(summary.click_events || 0) },
        {
          key: "unsubscribed",
          label: this.tt("newslettersAdmin.marketingUnsubscribed", "Desuscritos"),
          value: Number(summary.unsubscribed_emails || 0),
        },
      ];
    },
    emailMarketingCampaignRows() {
      const rows = this.emailMarketingSummary?.by_campaign_key;
      return Array.isArray(rows) ? rows : [];
    },
    emailMarketingBreakdownSections() {
      const summary = this.emailMarketingSummary || {};
      return [
        {
          key: "cycle",
          title: this.tt("newslettersAdmin.marketingByCycle", "Por ciclo"),
          rows: Array.isArray(summary.by_cycle) ? summary.by_cycle : [],
        },
        {
          key: "step",
          title: this.tt("newslettersAdmin.marketingByStep", "Por step"),
          rows: Array.isArray(summary.by_step) ? summary.by_step : [],
        },
        {
          key: "segment",
          title: this.tt("newslettersAdmin.marketingBySegment", "Por segmento"),
          rows: Array.isArray(summary.by_segment) ? summary.by_segment : [],
        },
        {
          key: "token_state",
          title: this.tt("newslettersAdmin.marketingByTokenState", "Por token_state"),
          rows: Array.isArray(summary.by_token_state) ? summary.by_token_state : [],
        },
      ];
    },
  },
  async mounted() {
    await this.checkAdminAccess();
    if (this.isAdmin) {
      await this.loadNewsletters();
    }
  },
  methods: {
    tt(key, fallback) {
      if (typeof this.$t !== "function") return fallback;
      const translated = this.$t(key);
      return typeof translated === "string" && translated !== key ? translated : fallback;
    },
    clearMessages() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    clearEmailMarketingMessages() {
      this.emailMarketingError = "";
      this.emailMarketingSuccess = "";
    },
    setActiveEmailTab(value) {
      this.activeEmailTab = value;
      if (value === "marketing" && !this.emailMarketingSummary && !this.emailMarketingLoading) {
        this.loadEmailMarketingDashboard();
      }
    },
    normalizeError(error, fallback) {
      const message = error?.message || "";
      if (typeof message === "string" && message.trim()) return message;
      return fallback;
    },
    async getSessionOrFail() {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session || !session.access_token) {
        throw new Error(this.tt("newslettersAdmin.authRequired", "Debes iniciar sesion para ver esta seccion."));
      }

      this.session = session;
      return session;
    },
    async checkAdminAccess() {
      try {
        const session = await this.getSessionOrFail();
        const currentUserStore = useCurrentUserStore();
        await currentUserStore.ensureSessionReady(session);
        if (!currentUserStore.hasAdminAccess) {
          this.$router.push("/create-clips");
          return;
        }
        this.isAdmin = true;
        this.isTwitchUser = currentUserStore.isTwitchUser;
        this.applyDefaultRecipient(currentUserStore);
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.authRequired", "Debes iniciar sesion para ver esta seccion.")
        );
        this.$router.push("/login");
      }
    },
    applyDefaultRecipient(currentUserStore) {
      const profile = currentUserStore.profile || {};
      const name = currentUserStore.displayName || profile.use_name || "Admin";
      const email = profile.user_email || profile.email || "";
      this.previewRecipient = { name, email };
      this.sendTestForm = { name, email };
      this.emailMarketingPreviewForm.recipient_name = name;
    },
    async loadEmailMarketingDashboard() {
      this.clearEmailMarketingMessages();
      this.emailMarketingLoading = true;
      this.emailMarketingStatesLoading = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        const [summary, states] = await Promise.all([
          fetchEmailMarketingSummary(session),
          fetchEmailMarketingStates(session, this.buildEmailMarketingStateParams()),
        ]);
        this.emailMarketingSummary = summary;
        this.applyEmailMarketingStatesPayload(states);
      } catch (error) {
        this.emailMarketingError = this.normalizeError(
          error,
          this.tt("newslettersAdmin.marketingLoadError", "No se pudo cargar Marketing Winback.")
        );
      } finally {
        this.emailMarketingLoading = false;
        this.emailMarketingStatesLoading = false;
      }
    },
    async loadEmailMarketingStates() {
      this.clearEmailMarketingMessages();
      this.emailMarketingStatesLoading = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        const payload = await fetchEmailMarketingStates(session, this.buildEmailMarketingStateParams());
        this.applyEmailMarketingStatesPayload(payload);
      } catch (error) {
        this.emailMarketingError = this.normalizeError(
          error,
          this.tt("newslettersAdmin.marketingStatesError", "No se pudieron cargar los estados de Marketing.")
        );
      } finally {
        this.emailMarketingStatesLoading = false;
      }
    },
    buildEmailMarketingStateParams() {
      return {
        status: this.emailMarketingStatusFilter,
        email: this.emailMarketingEmailFilter.trim(),
        limit: 50,
        offset: 0,
      };
    },
    applyEmailMarketingStatesPayload(payload) {
      this.emailMarketingStates = Array.isArray(payload?.items) ? payload.items : [];
      this.emailMarketingStatesTotal = Number(payload?.total || 0);
    },
    async setEmailMarketingStatusFilter(value) {
      this.emailMarketingStatusFilter = value;
      await this.loadEmailMarketingStates();
    },
    async searchEmailMarketingStates() {
      await this.loadEmailMarketingStates();
    },
    resetEmailMarketingStateFilters() {
      this.emailMarketingStatusFilter = "";
      this.emailMarketingEmailFilter = "";
      this.loadEmailMarketingStates();
    },
    openEmailMarketingPreviewModal() {
      this.clearEmailMarketingMessages();
      this.emailMarketingPreviewHtml = "";
      this.emailMarketingPreviewSubject = "";
      this.emailMarketingPreviewCampaignKey = "";
      this.showEmailMarketingPreviewModal = true;
    },
    closeEmailMarketingPreviewModal() {
      if (this.emailMarketingPreviewLoading) return;
      this.showEmailMarketingPreviewModal = false;
    },
    buildEmailMarketingPreviewPayload() {
      return {
        segment: this.emailMarketingPreviewForm.segment,
        cycle: Number(this.emailMarketingPreviewForm.cycle),
        step: Number(this.emailMarketingPreviewForm.step),
        token_state: this.emailMarketingPreviewForm.token_state,
        recipient_name: this.emailMarketingPreviewForm.recipient_name.trim(),
        unsubscribe_url: this.emailMarketingPreviewForm.unsubscribe_url.trim(),
      };
    },
    validateEmailMarketingPreviewPayload(payload) {
      if (!payload.recipient_name || !payload.unsubscribe_url) {
        this.emailMarketingError = this.tt(
          "newslettersAdmin.marketingPreviewValidation",
          "Nombre y URL de unsubscribe son obligatorios."
        );
        return false;
      }
      return true;
    },
    async runEmailMarketingPreview() {
      this.clearEmailMarketingMessages();
      const payload = this.buildEmailMarketingPreviewPayload();
      if (!this.validateEmailMarketingPreviewPayload(payload)) return;

      this.emailMarketingPreviewLoading = true;
      this.emailMarketingPreviewHtml = "";
      try {
        const session = this.session || await this.getSessionOrFail();
        const result = await previewEmailMarketing(session, payload);
        this.emailMarketingPreviewHtml = result.html || "";
        this.emailMarketingPreviewSubject = result.subject || "";
        this.emailMarketingPreviewCampaignKey = result.campaign_key || "";
      } catch (error) {
        this.emailMarketingError = this.normalizeError(
          error,
          this.tt("newslettersAdmin.marketingPreviewError", "No se pudo generar el preview de Marketing.")
        );
      } finally {
        this.emailMarketingPreviewLoading = false;
      }
    },
    emailMarketingStateStatusLabel(status) {
      const labels = {
        active: this.tt("newslettersAdmin.marketingStatusActive", "Activo"),
        cooldown: this.tt("newslettersAdmin.marketingStatusCooldown", "Cooldown"),
        converted: this.tt("newslettersAdmin.marketingStatusConverted", "Convertido"),
        exhausted: this.tt("newslettersAdmin.marketingStatusExhausted", "Exhausted"),
        paused: this.tt("newslettersAdmin.marketingStatusPaused", "Pausado"),
      };
      return labels[String(status || "").toLowerCase()] || status || "-";
    },
    emailMarketingStateStatusBadgeClass(status) {
      const normalized = String(status || "").toLowerCase();
      return {
        "badge-success": normalized === "active" || normalized === "converted",
        "badge-warning": normalized === "cooldown",
        "badge-secondary": normalized === "paused" || normalized === "exhausted",
      };
    },
    emailMarketingSegmentLabel(segment) {
      const labels = {
        generated: this.tt("newslettersAdmin.marketingSegmentGenerated", "Genero contenido"),
        not_generated: this.tt("newslettersAdmin.marketingSegmentNotGenerated", "Sin contenido generado"),
      };
      return labels[String(segment || "").toLowerCase()] || segment || "-";
    },
    emailMarketingTokenStateLabel(tokenState) {
      const labels = {
        has_tokens: this.tt("newslettersAdmin.marketingTokenHasTokens", "Con tokens"),
        no_tokens: this.tt("newslettersAdmin.marketingTokenNoTokens", "Sin tokens"),
      };
      return labels[String(tokenState || "").toLowerCase()] || tokenState || "-";
    },
    emailMarketingEngagementLabel(tier) {
      const labels = {
        cold: this.tt("newslettersAdmin.marketingEngagementCold", "Frio"),
        warm: this.tt("newslettersAdmin.marketingEngagementWarm", "Tibio"),
        engaged: this.tt("newslettersAdmin.marketingEngagementEngaged", "Engaged"),
      };
      return labels[String(tier || "").toLowerCase()] || tier || "-";
    },
    emailMarketingBreakdownLabel(type, key) {
      const value = String(key || "");
      if (type === "cycle") {
        return `${this.tt("newslettersAdmin.cycle", "Ciclo")} ${value}`;
      }
      if (type === "step") {
        return `${this.tt("newslettersAdmin.step", "Step")} ${value}`;
      }
      if (type === "segment") {
        return this.emailMarketingSegmentLabel(value);
      }
      if (type === "token_state") {
        return this.emailMarketingTokenStateLabel(value);
      }
      return value || "-";
    },
    async loadNewsletters() {
      this.clearMessages();
      this.loading = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        const payload = await fetchNewsletters(session, {
          status: this.statusFilter,
          limit: 100,
          offset: 0,
        });
        this.newsletters = Array.isArray(payload.items) ? payload.items : [];
        await this.loadNewsletterStats(session, this.newsletters);
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.loadError", "No se pudieron cargar newsletters.")
        );
      } finally {
        this.loading = false;
      }
    },
    async loadNewsletterStats(session, newsletters) {
      const ids = (newsletters || [])
        .filter((newsletter) => ["scheduled", "sending", "sent"].includes(String(newsletter.status || "")))
        .map((newsletter) => newsletter.id)
        .filter(Boolean);

      if (!ids.length) {
        this.newsletterStatsById = {};
        return;
      }

      try {
        const payload = await fetchNewsletterSummaries(session, ids);
        this.newsletterStatsById = payload?.items && typeof payload.items === "object" ? payload.items : {};
      } catch (error) {
        this.newsletterStatsById = {};
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.statsLoadError", "No se pudieron cargar stats de newsletters.")
        );
      }
    },
    buildEmptyDeliverySummary() {
      return {
        total: 0,
        pending: 0,
        sent: 0,
        failed: 0,
        opened_events: 0,
        click_events: 0,
        hard_bounce_events: 0,
        soft_bounce_events: 0,
        spam_events: 0,
        unsubscribed_events: 0,
      };
    },
    async setStatusFilter(value) {
      this.statusFilter = value;
      await this.loadNewsletters();
    },
    statusLabel(status) {
      const labels = {
        draft: this.tt("newslettersAdmin.statusDraft", "Borrador"),
        approved: this.tt("newslettersAdmin.statusApproved", "Aprobada"),
        rejected: this.tt("newslettersAdmin.statusRejected", "Rechazada"),
        scheduled: this.tt("newslettersAdmin.statusScheduled", "Programada"),
        sending: this.tt("newslettersAdmin.statusSending", "Enviando"),
        sent: this.tt("newslettersAdmin.statusSent", "Enviada"),
        cancelled: this.tt("newslettersAdmin.statusCancelled", "Cancelada"),
      };
      return labels[String(status || "").toLowerCase()] || status || "-";
    },
    statusBadgeClass(status) {
      const normalized = String(status || "").toLowerCase();
      return {
        "badge-secondary": normalized === "draft",
        "badge-success": normalized === "approved" || normalized === "sent",
        "badge-warning": normalized === "scheduled" || normalized === "sending",
        "badge-danger": normalized === "rejected" || normalized === "cancelled",
      };
    },
    deliveryStatusLabel(status) {
      const labels = {
        pending: this.tt("newslettersAdmin.pending", "Pendiente"),
        sent: this.tt("newslettersAdmin.sent", "Enviado"),
        failed: this.tt("newslettersAdmin.failed", "Fallido"),
      };
      return labels[String(status || "").toLowerCase()] || status || "-";
    },
    deliveryStatusBadgeClass(status) {
      const normalized = String(status || "").toLowerCase();
      return {
        "badge-warning": normalized === "pending",
        "badge-success": normalized === "sent",
        "badge-danger": normalized === "failed",
      };
    },
    canEdit(newsletter) {
      return String(newsletter?.status || "").toLowerCase() === "draft";
    },
    canApprove(newsletter) {
      return String(newsletter?.status || "").toLowerCase() === "draft";
    },
    canReject(newsletter) {
      return ["draft", "approved"].includes(String(newsletter?.status || "").toLowerCase());
    },
    canSchedule(newsletter) {
      return String(newsletter?.status || "").toLowerCase() === "approved";
    },
    canDelete(newsletter) {
      return ["draft", "rejected", "approved", "cancelled"].includes(String(newsletter?.status || "").toLowerCase());
    },
    isActionRunning(newsletter) {
      return this.actionNewsletterId === newsletter.id || this.saving;
    },
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString();
    },
    openGenerateModal() {
      this.clearMessages();
      this.generateForm = buildDefaultGenerateForm();
      this.showGenerateModal = true;
    },
    closeGenerateModal() {
      if (this.saving) return;
      this.showGenerateModal = false;
    },
    buildGeneratePayload() {
      const topics = String(this.generateForm.topicsText || "")
        .split(/\r?\n/)
        .map((topic) => topic.trim())
        .filter(Boolean);

      return {
        count: Number(this.generateForm.count),
        language: this.generateForm.language,
        topics,
      };
    },
    validateGenerateForm(payload) {
      if (!Number.isInteger(payload.count) || payload.count < 1 || payload.count > 30) {
        this.errorMessage = this.tt("newslettersAdmin.generateCountValidation", "La cantidad debe estar entre 1 y 30.");
        return false;
      }
      if (!["es", "en"].includes(payload.language)) {
        this.errorMessage = this.tt("newslettersAdmin.generateLanguageValidation", "Selecciona un idioma valido.");
        return false;
      }
      if (!payload.topics.length) {
        this.errorMessage = this.tt("newslettersAdmin.generateTopicsValidation", "Agrega al menos un tema.");
        return false;
      }
      return true;
    },
    async generateDrafts() {
      this.clearMessages();
      const payload = this.buildGeneratePayload();
      if (!this.validateGenerateForm(payload)) return;

      this.saving = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        const result = await generateNewsletters(session, payload);
        const total = Number(result.total || payload.count);
        this.showGenerateModal = false;
        this.statusFilter = "draft";
        await this.loadNewsletters();
        this.successMessage = this.tt("newslettersAdmin.generateSuccess", "Newsletters generadas correctamente.")
          .replace("{count}", String(total));
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.generateError", "No se pudieron generar newsletters.")
        );
      } finally {
        this.saving = false;
      }
    },
    openCreateModal() {
      this.clearMessages();
      this.isEditing = false;
      this.selectedNewsletter = null;
      this.editorForm = buildEditorForm();
      this.showEditorModal = true;
    },
    openEditModal(newsletter) {
      if (!this.canEdit(newsletter)) return;
      this.clearMessages();
      this.isEditing = true;
      this.selectedNewsletter = newsletter;
      this.editorForm = buildEditorForm(newsletter);
      this.showEditorModal = true;
    },
    closeEditorModal() {
      if (this.saving) return;
      this.showEditorModal = false;
    },
    validateEditorForm() {
      if (!this.editorForm.title.trim() || !this.editorForm.subject.trim()) {
        this.errorMessage = this.tt("newslettersAdmin.validationTitleSubject", "Titulo y asunto son obligatorios.");
        return false;
      }

      const missingField = getContentFields().find((field) => {
        const value = this.editorForm.content_json[field.key];
        return value === undefined || value === null || String(value).trim() === "";
      });

      if (missingField) {
        this.errorMessage = this.tt("newslettersAdmin.validationContent", "Completa todos los campos de contenido.");
        return false;
      }

      return true;
    },
    buildNewsletterPayload() {
      const contentJson = {};
      getContentFields().forEach((field) => {
        contentJson[field.key] = String(this.editorForm.content_json[field.key] || "").trim();
      });
      contentJson.section_2_link = "https://app.clipr.tv";
      contentJson.section_3_link = "https://app.clipr.tv";

      return {
        title: this.editorForm.title.trim(),
        subject: this.editorForm.subject.trim(),
        content_json: contentJson,
      };
    },
    async saveNewsletter() {
      this.clearMessages();
      if (!this.validateEditorForm()) return;

      this.saving = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        const payload = this.buildNewsletterPayload();
        if (this.isEditing && this.selectedNewsletter) {
          await updateNewsletter(session, this.selectedNewsletter.id, payload);
          this.successMessage = this.tt("newslettersAdmin.updateSuccess", "Newsletter actualizada correctamente.");
        } else {
          await createNewsletter(session, payload);
          this.successMessage = this.tt("newslettersAdmin.createSuccess", "Newsletter creada correctamente.");
        }
        this.showEditorModal = false;
        await this.loadNewsletters();
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.saveError", "No se pudo guardar la newsletter.")
        );
      } finally {
        this.saving = false;
      }
    },
    async runNewsletterAction(newsletter, action, successMessage, fallbackMessage) {
      this.clearMessages();
      this.actionNewsletterId = newsletter.id;
      try {
        const session = this.session || await this.getSessionOrFail();
        await action(session, newsletter.id);
        this.successMessage = successMessage;
        await this.loadNewsletters();
      } catch (error) {
        this.errorMessage = this.normalizeError(error, fallbackMessage);
      } finally {
        this.actionNewsletterId = null;
      }
    },
    async approveItem(newsletter) {
      if (!this.canApprove(newsletter)) return;
      await this.runNewsletterAction(
        newsletter,
        approveNewsletter,
        this.tt("newslettersAdmin.approveSuccess", "Newsletter aprobada correctamente."),
        this.tt("newslettersAdmin.approveError", "No se pudo aprobar la newsletter.")
      );
    },
    async rejectItem(newsletter) {
      if (!this.canReject(newsletter)) return;
      await this.runNewsletterAction(
        newsletter,
        rejectNewsletter,
        this.tt("newslettersAdmin.rejectSuccess", "Newsletter rechazada correctamente."),
        this.tt("newslettersAdmin.rejectError", "No se pudo rechazar la newsletter.")
      );
    },
    openDeleteModal(newsletter) {
      if (!this.canDelete(newsletter)) return;
      this.clearMessages();
      this.selectedNewsletter = newsletter;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      if (this.saving) return;
      this.showDeleteModal = false;
    },
    async deleteSelectedNewsletter() {
      if (!this.selectedNewsletter || !this.canDelete(this.selectedNewsletter)) return;

      const newsletter = this.selectedNewsletter;
      this.clearMessages();
      this.saving = true;
      this.actionNewsletterId = newsletter.id;
      try {
        const session = this.session || await this.getSessionOrFail();
        await deleteNewsletter(session, newsletter.id);
        this.showDeleteModal = false;
        this.selectedNewsletter = null;
        await this.loadNewsletters();
        this.successMessage = this.tt("newslettersAdmin.deleteSuccess", "Newsletter eliminada correctamente.");
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.deleteError", "No se pudo eliminar la newsletter.")
        );
      } finally {
        this.saving = false;
        this.actionNewsletterId = null;
      }
    },
    async openPreviewModal(newsletter) {
      this.clearMessages();
      this.selectedNewsletter = newsletter;
      this.previewHtml = "";
      this.showPreviewModal = true;
      await this.loadPreviewHtml();
    },
    closePreviewModal() {
      this.showPreviewModal = false;
      this.previewHtml = "";
    },
    async loadPreviewHtml() {
      if (!this.selectedNewsletter) return;
      if (!this.previewRecipient.name.trim() || !this.previewRecipient.email.trim()) {
        this.errorMessage = this.tt("newslettersAdmin.previewValidation", "Nombre y email de preview son obligatorios.");
        return;
      }

      this.previewLoading = true;
      this.errorMessage = "";
      try {
        const session = this.session || await this.getSessionOrFail();
        const payload = await fetchNewsletterPreview(session, this.selectedNewsletter.id, {
          name: this.previewRecipient.name,
          email: this.previewRecipient.email,
        });
        this.previewHtml = payload.html || "";
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.previewError", "No se pudo generar el preview.")
        );
      } finally {
        this.previewLoading = false;
      }
    },
    openSendTestModal(newsletter) {
      this.clearMessages();
      this.selectedNewsletter = newsletter;
      this.showSendTestModal = true;
    },
    closeSendTestModal() {
      if (this.saving) return;
      this.showSendTestModal = false;
    },
    async sendTestEmail() {
      if (!this.selectedNewsletter) return;
      this.clearMessages();
      this.saving = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        await sendNewsletterTest(session, this.selectedNewsletter.id, {
          name: this.sendTestForm.name,
          email: this.sendTestForm.email,
        });
        this.successMessage = this.tt("newslettersAdmin.sendTestSuccess", "Email de prueba enviado correctamente.");
        this.showSendTestModal = false;
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.sendTestError", "No se pudo enviar el email de prueba.")
        );
      } finally {
        this.saving = false;
      }
    },
    openScheduleModal(newsletter) {
      if (!this.canSchedule(newsletter)) return;
      this.clearMessages();
      this.selectedNewsletter = newsletter;
      this.scheduleForm.scheduled_at = buildDefaultScheduleLocal();
      this.showScheduleModal = true;
    },
    closeScheduleModal() {
      if (this.saving) return;
      this.showScheduleModal = false;
    },
    async scheduleSelectedNewsletter() {
      if (!this.selectedNewsletter) return;
      this.clearMessages();
      const scheduledAt = new Date(this.scheduleForm.scheduled_at);
      if (Number.isNaN(scheduledAt.getTime())) {
        this.errorMessage = this.tt("newslettersAdmin.scheduleValidation", "Selecciona una fecha valida.");
        return;
      }

      this.saving = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        await scheduleNewsletter(session, this.selectedNewsletter.id, {
          scheduled_at: scheduledAt.toISOString(),
        });
        this.successMessage = this.tt("newslettersAdmin.scheduleSuccess", "Newsletter programada correctamente.");
        this.showScheduleModal = false;
        await this.loadNewsletters();
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.scheduleError", "No se pudo programar la newsletter.")
        );
      } finally {
        this.saving = false;
      }
    },
    async openDeliveriesModal(newsletter) {
      this.clearMessages();
      this.selectedNewsletter = newsletter;
      this.selectedDeliveries = [];
      this.selectedDeliverySummary = this.buildEmptyDeliverySummary();
      this.showDeliveriesModal = true;
      await this.loadDeliveries();
    },
    closeDeliveriesModal() {
      this.showDeliveriesModal = false;
      this.selectedDeliveries = [];
      this.selectedDeliverySummary = this.buildEmptyDeliverySummary();
    },
    async loadDeliveries() {
      if (!this.selectedNewsletter) return;

      this.deliveriesLoading = true;
      try {
        const session = this.session || await this.getSessionOrFail();
        const newsletterId = this.selectedNewsletter.id;
        const [summary, deliveries] = await Promise.all([
          fetchNewsletterSummary(session, newsletterId),
          (async () => {
            const rows = [];
            let offset = 0;
            let page = 0;
            let shouldContinue = true;

            while (shouldContinue && page < DELIVERY_MAX_PAGES) {
              const payload = await fetchNewsletterDeliveries(session, newsletterId, {
                limit: DELIVERY_PAGE_SIZE,
                offset,
              });
              const pageRows = Array.isArray(payload.items) ? payload.items : [];
              rows.push(...pageRows);
              shouldContinue = pageRows.length === DELIVERY_PAGE_SIZE;
              offset += DELIVERY_PAGE_SIZE;
              page += 1;
            }

            return rows;
          })(),
        ]);

        this.selectedDeliverySummary = {
          ...this.buildEmptyDeliverySummary(),
          ...(summary || {}),
        };
        this.selectedDeliveries = deliveries;
        if (summary) {
          this.newsletterStatsById = {
            ...this.newsletterStatsById,
            [newsletterId]: summary,
          };
        }
      } catch (error) {
        this.errorMessage = this.normalizeError(
          error,
          this.tt("newslettersAdmin.deliveriesError", "No se pudieron cargar deliveries.")
        );
      } finally {
        this.deliveriesLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.newsletters-admin-page {
  padding-bottom: 32px;
}

.newsletters-card {
  overflow: hidden;
}

.newsletters-header {
  gap: 16px;
}

.newsletters-subtitle {
  color: #64748b;
  font-size: 0.85rem;
}

.newsletters-header-actions {
  gap: 8px;
}

.newsletters-body {
  padding: 0 1.25rem 1.25rem;
}

.email-admin-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.email-admin-tab {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  color: #475569;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 8px 16px;
  transition: var(--clipr-transition, all 0.25s ease);
}

.email-admin-tab:hover {
  border-color: #94a3b8;
  color: #0f172a;
}

.email-admin-tab.active {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  box-shadow: 0 4px 16px var(--clipr-purple-glow);
  color: #fff;
}

.email-tab-placeholder {
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  color: #64748b;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 260px;
  padding: 28px;
  text-align: center;
}

.email-tab-placeholder i {
  color: #94a3b8;
  font-size: 2.2rem;
  margin-bottom: 12px;
}

.email-tab-placeholder h5 {
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 8px;
}

.email-tab-placeholder p {
  margin-bottom: 0;
}

.try-marketing-panel {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  padding: 18px;
}

.try-marketing-header {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.try-marketing-header h5 {
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 6px;
}

.try-marketing-header p {
  color: #64748b;
  margin-bottom: 0;
}

.try-marketing-metrics {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  margin-bottom: 16px;
}

.try-marketing-metric {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  padding: 14px;
}

.try-marketing-metric-value {
  color: #0f172a;
  display: block;
  font-size: 1.55rem;
  font-weight: 800;
  line-height: 1;
}

.try-marketing-metric-label {
  color: #64748b;
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 8px;
  text-transform: uppercase;
}

.try-marketing-dates {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.84rem;
  gap: 12px;
  margin-bottom: 16px;
}

.try-marketing-table {
  margin-top: 4px;
}

.try-marketing-campaign-key {
  color: #0f172a;
  font-weight: 700;
}

.email-marketing-header-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.email-marketing-actions-card {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 14px;
}

.email-marketing-actions-card h6 {
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 4px;
}

.email-marketing-actions-card p {
  color: #64748b;
  font-size: 0.84rem;
  margin-bottom: 0;
}

.email-marketing-sync-form,
.email-marketing-state-search {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.email-marketing-sync-form .form-control,
.email-marketing-state-search .form-control {
  min-width: 260px;
}

.email-marketing-sync-result {
  margin-bottom: 16px;
}

.email-marketing-sync-summary {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.84rem;
  gap: 12px;
  margin-bottom: 10px;
}

.email-marketing-result-table,
.email-marketing-state-filters,
.email-marketing-states-table {
  margin-top: 16px;
}

.email-marketing-breakdowns {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  margin-top: 16px;
}

.email-marketing-breakdown-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  padding: 14px;
}

.email-marketing-breakdown-card h6 {
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 12px;
}

.email-marketing-breakdown-empty {
  color: #64748b;
  font-size: 0.84rem;
}

.email-marketing-email {
  min-width: 240px;
}

.email-marketing-preview-meta {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.84rem;
  gap: 12px;
  margin: 18px 0;
}

.email-marketing-preview-frame {
  margin-top: 18px;
}

.newsletters-filters {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  margin-bottom: 18px;
  padding: 14px 16px;
}

.newsletters-filter-label {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.newsletters-filter-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.newsletters-filter-tab {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  color: #475569;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 7px 14px;
  transition: var(--clipr-transition, all 0.25s ease);
}

.newsletters-filter-tab:hover {
  border-color: #94a3b8;
  color: #0f172a;
}

.newsletters-filter-tab.active {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  box-shadow: 0 4px 16px var(--clipr-purple-glow);
  color: #fff;
}

.newsletters-state {
  align-items: center;
  color: #64748b;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 240px;
  text-align: center;
}

.newsletters-state i {
  color: #94a3b8;
  font-size: 2.5rem;
  margin-bottom: 12px;
}

.newsletters-state-compact {
  min-height: 140px;
}

.newsletters-table-responsive {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: var(--clipr-radius-lg, 14px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: auto;
}

.newsletters-table {
  margin-bottom: 0 !important;
}

.newsletters-table thead th {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
  border-top: 0;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 12px 14px;
  text-transform: uppercase;
  white-space: nowrap;
}

.newsletters-table tbody td {
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.85rem;
  padding: 12px 14px;
  vertical-align: middle;
}

.newsletters-table tbody tr:hover {
  background-color: #f8fafc;
}

.newsletters-table tbody tr:last-child td {
  border-bottom: none;
}

.newsletter-title {
  color: #0f172a;
  font-weight: 700;
}

.newsletter-subject {
  color: #334155;
  max-width: 260px;
}

.newsletter-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 360px;
}

.newsletter-action-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.2;
  padding: 4px 10px;
  transition: var(--clipr-transition, all 0.25s ease);
  white-space: nowrap;
}

.newsletter-action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.newsletter-action-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border-color: rgba(15, 23, 42, 0.12);
  color: #475569;
}

.newsletter-action-btn-muted:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.08);
  border-color: rgba(15, 23, 42, 0.18);
  color: #0f172a;
}

.newsletter-action-btn-outline {
  background: rgba(168, 85, 247, 0.06);
  border-color: rgba(168, 85, 247, 0.28);
  color: #7e22ce;
}

.newsletter-action-btn-outline:hover:not(:disabled) {
  background: rgba(168, 85, 247, 0.12);
  border-color: rgba(168, 85, 247, 0.42);
  color: #6b21a8;
}

.newsletter-action-btn-success {
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.28);
  color: #15803d;
}

.newsletter-action-btn-success:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.42);
  color: #166534;
}

.newsletter-action-btn-warning {
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(245, 158, 11, 0.32);
  color: #b45309;
}

.newsletter-action-btn-warning:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.22);
  border-color: rgba(245, 158, 11, 0.46);
  color: #92400e;
}

.newsletter-action-btn-primary {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  box-shadow: 0 4px 14px var(--clipr-purple-glow);
  color: #fff;
}

.newsletter-action-btn-primary:hover:not(:disabled) {
  box-shadow: 0 6px 18px var(--clipr-purple-overlay-bold);
  transform: translateY(-1px);
}

.newsletter-action-btn-accent {
  background: var(--clipr-citrus-gradient);
  border-color: transparent;
  box-shadow: 0 4px 14px var(--clipr-citrus-glow);
  color: #fff;
}

.newsletter-action-btn-accent:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(217, 119, 6, 0.35);
  transform: translateY(-1px);
}

.newsletter-action-btn-danger {
  background: rgba(220, 38, 38, 0.08);
  border-color: rgba(220, 38, 38, 0.28);
  color: #b91c1c;
}

.newsletter-action-btn-danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.16);
  border-color: rgba(220, 38, 38, 0.42);
  color: #991b1b;
}

.newsletter-status-badge {
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  min-width: 88px;
  padding: 0.35em 0.75em;
}

.newsletters-modal-backdrop {
  align-items: center;
  background: rgba(9, 9, 11, 0.62);
  backdrop-filter: blur(4px);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  overflow-y: auto;
  padding: 28px 16px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1050;
}

.newsletters-modal {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: var(--clipr-radius-xl, 18px);
  box-shadow: 0 22px 80px rgba(15, 23, 42, 0.18), 0 0 30px var(--clipr-citrus-glow);
  max-width: 680px;
  overflow: hidden;
  width: 100%;
}

.newsletters-modal-xl {
  max-width: 1120px;
}

.newsletters-modal-header {
  align-items: flex-start;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  padding: 18px 22px;
}

.newsletters-modal-header h5 {
  color: #0f172a;
  font-weight: 700;
}

.newsletters-modal-header p {
  color: #64748b;
  font-size: 0.9rem;
}

.newsletters-modal-body {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
  padding: 22px;
}

.newsletters-modal-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin: 22px -22px -22px;
  padding: 16px 22px;
}

.newsletters-editor-form label,
.newsletters-generate-form label,
.email-marketing-preview-form label {
  color: #475569;
  font-size: 0.82rem;
  font-weight: 700;
  margin-top: 12px;
}

.newsletters-editor-group {
  border-top: 1px solid #e2e8f0;
  margin-top: 18px;
  padding-top: 18px;
}

.newsletters-editor-group h6 {
  color: #9333ea;
  font-weight: 700;
  margin-bottom: 2px;
}

.newsletters-preview-controls {
  margin-bottom: 18px;
}

.newsletters-preview-frame {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  height: 640px;
  width: 100%;
}

.newsletters-delivery-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.newsletter-stats-compact {
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.02em;
}

.newsletter-stats-compact span {
  background: #f1f5f9;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
}

.newsletters-deliveries-table {
  border: 1px solid #e2e8f0;
  border-radius: var(--clipr-radius-lg, 14px);
  max-height: 520px;
  overflow-y: auto;
}

.newsletters-delete-warning {
  color: #475569;
  margin-bottom: 0;
}

.delivery-error {
  max-width: 360px;
  white-space: normal;
  word-break: break-word;
}

/* Tema Clipr admin (claro, acentos cítricos + púrpura) */
.newsletters-admin-page.clipr-theme :deep(.card) {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08), 0 0 20px var(--clipr-citrus-glow) !important;
  color: #212529 !important;
}

.newsletters-admin-page.clipr-theme :deep(.card-title) {
  color: #0f172a !important;
  font-weight: 700;
}

.newsletters-admin-page.clipr-theme :deep(.btn-primary) {
  background: var(--clipr-citrus-gradient) !important;
  border: none !important;
  box-shadow: 0 8px 30px var(--clipr-citrus-glow) !important;
  color: #fff !important;
}

.newsletters-admin-page.clipr-theme :deep(.btn-primary:hover) {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  box-shadow: 0 12px 40px rgba(217, 119, 6, 0.4) !important;
  transform: translateY(-1px);
}

.newsletters-admin-page.clipr-theme :deep(.btn-outline-primary) {
  border-color: rgba(168, 85, 247, 0.45) !important;
  color: #9333ea !important;
}

.newsletters-admin-page.clipr-theme :deep(.btn-outline-primary:hover) {
  background: rgba(168, 85, 247, 0.08) !important;
  border-color: #a855f7 !important;
  color: #7e22ce !important;
}

.newsletters-admin-page.clipr-theme :deep(.form-control) {
  background: #ffffff !important;
  border-color: #dee2e6 !important;
  color: #212529 !important;
}

.newsletters-admin-page.clipr-theme :deep(.form-control:focus) {
  border-color: var(--clipr-citrus-yellow) !important;
  box-shadow: 0 0 0 0.2rem var(--clipr-citrus-glow) !important;
}

.newsletters-admin-page.clipr-theme .newsletters-table .text-muted {
  color: #64748b !important;
}

.newsletters-admin-page.clipr-theme .newsletters-modal :deep(.close) {
  color: #64748b;
  opacity: 1;
  text-shadow: none;
}

.newsletters-admin-page.clipr-theme .newsletters-modal :deep(.close:hover) {
  color: #0f172a;
}

.newsletters-admin-page.clipr-theme :deep(.btn-danger) {
  background: #dc2626 !important;
  border-color: #dc2626 !important;
  color: #fff !important;
}

.newsletters-admin-page.clipr-theme :deep(.btn-danger:hover) {
  background: #b91c1c !important;
  border-color: #b91c1c !important;
}

/* Tema Twitch (oscuro, acentos púrpura) */
.newsletters-admin-page.clipr-theme.twitch-theme :deep(.card) {
  background: var(--clipr-bg-card) !important;
  border: 1px solid var(--clipr-border-color) !important;
  box-shadow: var(--clipr-shadow-card) !important;
  color: var(--clipr-text-primary) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.card-title) {
  color: var(--clipr-text-primary) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-subtitle,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal-header p,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-state {
  color: var(--clipr-text-secondary) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-filters {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
}

.newsletters-admin-page.clipr-theme.twitch-theme .email-admin-tab {
  background: transparent;
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .email-admin-tab:hover {
  border-color: var(--clipr-border-hover);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .email-admin-tab.active {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  box-shadow: 0 4px 16px var(--clipr-purple-glow);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .email-tab-placeholder {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .email-tab-placeholder h5 {
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-panel {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-header h5,
.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-metric-value,
.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-campaign-key,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-actions-card h6,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-breakdown-card h6 {
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-header p,
.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-metric-label,
.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-dates,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-actions-card p,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-sync-summary,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-preview-meta,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-breakdown-empty {
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .try-marketing-metric,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-actions-card,
.newsletters-admin-page.clipr-theme.twitch-theme .email-marketing-breakdown-card {
  background: var(--clipr-bg-tertiary);
  border-color: var(--clipr-border-color);
  box-shadow: none;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-filter-label {
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-filter-tab {
  background: transparent;
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-filter-tab:hover {
  border-color: var(--clipr-border-hover);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-filter-tab.active {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  box-shadow: 0 4px 16px var(--clipr-purple-glow);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-table-responsive {
  border-color: var(--clipr-border-color);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5), 0 0 20px var(--clipr-purple-glow);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-table thead th {
  background: rgba(168, 85, 247, 0.15);
  border-bottom-color: var(--clipr-border-purple);
  color: var(--clipr-purple-light);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-table tbody td {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-table tbody tr:hover {
  background: var(--clipr-purple-overlay-faint);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-title {
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-stats-compact span {
  background: rgba(148, 163, 184, 0.16);
  color: #cbd5e1;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-subject {
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-delete-warning {
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-table .text-muted {
  color: var(--clipr-text-secondary) !important;
  opacity: 1;
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.btn-primary) {
  background: var(--clipr-purple-gradient) !important;
  box-shadow: var(--clipr-shadow-button) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.btn-primary:hover) {
  background: linear-gradient(135deg, #a855f7 0%, #d946ef 100%) !important;
  box-shadow: var(--clipr-shadow-button-hover) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.btn-outline-primary) {
  border-color: var(--clipr-border-purple) !important;
  color: var(--clipr-purple-light) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.btn-outline-primary:hover) {
  background: var(--clipr-purple-overlay-light) !important;
  color: var(--clipr-purple-light) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  box-shadow: var(--clipr-shadow-card);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal-header,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal-footer,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-editor-group,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-preview-frame,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-deliveries-table {
  border-color: var(--clipr-border-color);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-deliveries-table .newsletters-table thead th {
  background: rgba(168, 85, 247, 0.15);
  border-bottom-color: var(--clipr-border-purple);
  color: var(--clipr-purple-light);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-deliveries-table .newsletters-table tbody td {
  background: var(--clipr-bg-elevated);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-deliveries-table .newsletters-table tbody tr:hover {
  background: var(--clipr-purple-overlay-faint);
}

.newsletters-admin-page.clipr-theme.twitch-theme .delivery-error {
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-muted {
  background: rgba(255, 255, 255, 0.04);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-muted:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--clipr-border-hover);
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-outline {
  background: var(--clipr-purple-overlay-light);
  border-color: var(--clipr-border-purple);
  color: var(--clipr-purple-light);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-outline:hover:not(:disabled) {
  background: var(--clipr-purple-overlay-medium);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-success {
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(74, 222, 128, 0.28);
  color: #86efac;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-success:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.24);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-warning {
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(251, 191, 36, 0.28);
  color: #fcd34d;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-warning:hover:not(:disabled) {
  background: rgba(245, 158, 11, 0.24);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-primary {
  background: var(--clipr-purple-gradient);
  box-shadow: 0 4px 14px var(--clipr-purple-glow);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-accent {
  background: var(--clipr-citrus-gradient);
  box-shadow: 0 4px 14px var(--clipr-citrus-glow);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-danger {
  background: rgba(220, 38, 38, 0.12);
  border-color: rgba(248, 113, 113, 0.28);
  color: #fca5a5;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletter-action-btn-danger:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.22);
  color: #fff;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal-header h5,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-editor-form label,
.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-generate-form label {
  color: var(--clipr-text-primary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal-footer {
  background: var(--clipr-bg-tertiary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-editor-group h6 {
  color: var(--clipr-purple-light);
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.form-control) {
  background: var(--clipr-bg-tertiary) !important;
  border-color: var(--clipr-border-color) !important;
  color: var(--clipr-text-primary) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme :deep(.form-control:focus) {
  border-color: var(--clipr-border-purple) !important;
  box-shadow: 0 0 0 0.2rem var(--clipr-purple-glow) !important;
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal :deep(.close) {
  color: var(--clipr-text-secondary);
}

.newsletters-admin-page.clipr-theme.twitch-theme .newsletters-modal :deep(.close:hover) {
  color: var(--clipr-text-primary);
}

@media (max-width: 767.98px) {
  .newsletters-body {
    padding: 0 1rem 1rem;
  }

  .newsletters-header-actions {
    width: 100%;
  }

  .newsletter-actions {
    max-width: none;
  }
}
</style>
